import { dehydrate } from '@tanstack/react-query';
import { getQueryClient, redirect } from 'common-nextjs';
import { getSession, isLoggedIn } from 'fetcher-session';
import { useRouter } from 'next/router';
import { ParsedUrlQuery } from 'querystring';
import { useEffect } from 'react';
import {
  getItemDetailIds,
  postDuplicateItemById,
  postImageRecognitionUri,
} from '~/api/listFlow';
import { fetchModelById } from '~/api/models';
import Layout from '~/components/Layout';
import ListFlowEbaySection from '~/components/ListFlow/EbaySection/ListFlowEbaySection';
import ListFlowInfoSection from '~/components/ListFlow/InfoSection/ListFlowInfoSection';
import ListFlowSubmitButtons from '~/components/ListFlow/ListFlowSubmitButtons';
import ListFlowPhotoSection from '~/components/ListFlow/PhotoSection/ListFlowPhotoSection';
import ListFlowPriceSection from '~/components/ListFlow/PriceSection/ListFlowPriceSection';
import ListFlowPrivateDetailsSection from '~/components/ListFlow/PrivateDetailsSection/ListFlowPrivateDetailsSection';
import ListFlowShippingSection from '~/components/ListFlow/ShippingSection/ListFlowShippingSection';
import WhiteLabelSoftLanding from '~/components/ListFlow/WhiteLabelSoftLanding';
import {
  prefetchListFlowCategories,
  prefetchListFlowCategory,
  prefetchListFlowParcel,
} from '~/hooks/listFlow/useListFlowCategories';
import { prefetchListFlowDetails } from '~/hooks/listFlow/useListFlowDetails';
import { prefetchListFlowItem } from '~/hooks/listFlow/useListFlowItem';
import { prefetchAddress } from '~/hooks/useAddress';
import { fireVisitedListFlow } from '~/services/analytics/events/listFlow';
import {
  finishInitializingItem,
  initializeFromItem,
  startInitializationPhase,
} from '~/store/listFlow/initializationActions';
import {
  changeDetail,
  changeModel,
  initializeCategory,
  initializeDetails,
} from '~/store/listFlow/slices/info';
import {
  changedDefaultParcel,
  changedShipsFromCountry,
} from '~/store/listFlow/slices/shipping';
import { reduxWrapper } from '~/store/store';
import { CustomNextPage } from '~/typings/custom-next';

interface ListFlowParams extends ParsedUrlQuery {
  id?: string;
  prefill?: '1';
  category?: string;
  recognition_uri?: string;
  duplicate_id?: string;
}

interface Props {
  id: number | null;
}

const ItemListFlowPage: CustomNextPage<Props> = ({ id }) => {
  useEffect(() => {
    fireVisitedListFlow();
    window?.zE?.('webWidget', 'hide');
    return () => window?.zE?.('webWidget', 'show');
  }, []);

  const router = useRouter();

  return (
    <Layout title="List Flow" excludeMobileSearch simpleFooter>
      {router.query.from_wlm && <WhiteLabelSoftLanding />}
      <ListFlowInfoSection />
      <ListFlowPhotoSection />
      <ListFlowPriceSection />
      <ListFlowPrivateDetailsSection />
      <ListFlowEbaySection />
      <ListFlowShippingSection />
      <ListFlowSubmitButtons />
    </Layout>
  );
};

ItemListFlowPage.getInitialProps = reduxWrapper.getInitialPageProps(
  store => async ctx => {
    const dispatch = store.dispatch;
    const session = await getSession(ctx);

    const queryClient = getQueryClient();
    const query = ctx.query as ListFlowParams;
    const id = Number(query.id);

    const duplicateId = Number(query.duplicate_id);
    if (duplicateId) {
      try {
        const newItem = await postDuplicateItemById.call(ctx, duplicateId);
        await redirect(ctx, `/items/${newItem.id}?from_duplicate=1`);
        return {};
      } catch (err) {
        throw await redirect(
          ctx,
          '/items/new?message=Failed+to+duplicate+that+item...',
        );
      }
    }

    try {
      await prefetchListFlowCategories(queryClient);
    } catch (e) {}

    dispatch(startInitializationPhase(id));

    if (isLoggedIn(session)) {
      try {
        const address = await prefetchAddress(ctx, queryClient, 'shipFrom');
        if (address?.country) {
          dispatch(changedShipsFromCountry(address.country));
        }
      } catch (e) {}
    }

    if (id) {
      // These insane try/catches are here because while Immer is a great library,
      // it's frustratingly maintained. I've had so many issues with it tbh.
      // If these aren't here, the entire process will be interrupted, even
      // though it's actually successful.
      try {
        const item = await prefetchListFlowItem(ctx, queryClient, id);

        if (item) {
          const categoryId = item.categories?.[0]?.id;
          if (categoryId) {
            const [details, category, parcel] = await Promise.all([
              prefetchListFlowDetails(queryClient, categoryId),
              prefetchListFlowCategory(queryClient, categoryId),
              prefetchListFlowParcel(
                queryClient,
                categoryId,
                getItemDetailIds(item),
              ),
            ] as const);

            try {
              if (details) {
                dispatch(initializeDetails(details));
              }
            } catch (e) {}

            try {
              if (category) {
                dispatch(initializeCategory(category));
              }
            } catch (e) {}

            try {
              if (parcel) {
                dispatch(changedDefaultParcel(parcel));
              }
            } catch (e) {}
          }

          try {
            dispatch(initializeFromItem(item));
          } catch (e) {}
        } else {
          // Something went wrong initializing the item...
        }
      } catch (e) {}
    }

    try {
      if (query.prefill === '1' || query.prefill_data === '1') {
        const categoryId = Number(query.category);

        if (categoryId) {
          const [details, category, parcel] = await Promise.all([
            prefetchListFlowDetails(queryClient, categoryId),
            prefetchListFlowCategory(queryClient, categoryId),
            prefetchListFlowParcel(
              queryClient,
              categoryId,

              // Map all values to numbers, but skip the known keys.
              Object.entries(query)
                .filter(
                  ([key]) =>
                    key !== 'category' &&
                    key !== 'prefill' &&
                    key !== 'prefill_data' &&
                    key !== 'recognition_uri' &&
                    key !== 'duplicate_id' &&
                    key !== 'id' &&
                    key !== 'from_wlm',
                )
                .map(([_, value]) => Number(value)),
            ),
          ] as const);

          if (details) {
            dispatch(initializeDetails(details));
          }

          if (parcel) {
            dispatch(changedDefaultParcel(parcel));
          }

          // If those look good, go over the prefill
          if (category) {
            dispatch(initializeCategory(category));

            // If there's an image recognition URI, go get it and then map those details
            if (details && query.recognition_uri) {
              const recognitionResponse = await postImageRecognitionUri.call(
                ctx,
                query.recognition_uri,
              );

              if (
                recognitionResponse &&
                recognitionResponse.details.length > 0
              ) {
                for (let i = 0; i < recognitionResponse.details.length; i++) {
                  const item = recognitionResponse.details[i];

                  // Just need to make sure the details are fully loaded first.
                  const detail = details.find(d => d.slug === item.type);
                  if (detail && detail.children && detail.children.length > 0) {
                    const selectedChild = detail.children.find(
                      child => child.id === item.id,
                    );
                    if (selectedChild) {
                      dispatch(
                        changeDetail({
                          detail,
                          selected: {
                            ...selectedChild,
                            imageRecognitionConfidence: item.confidence,
                          },
                        }),
                      );
                    }
                  }
                }
              }
            } else if (details) {
              const entries = Object.entries(query);
              for (let i = 0; i < entries.length; i++) {
                const [key, value] = entries[i];
                const valueId = Number(value);
                if (!valueId) {
                  continue;
                }

                // Just need to make sure the details are fully loaded first.
                const detail = details.find(d => d.slug === key);
                if (detail && detail.children && detail.children.length > 0) {
                  const selectedChild = detail.children.find(
                    child => child.id === valueId,
                  );
                  if (selectedChild) {
                    dispatch(
                      changeDetail({
                        detail,
                        selected: selectedChild,
                      }),
                    );
                  }
                }
              }

              // After parsing details, check for model. We want this to be last since any brand changes will
              // remove the model. Could've sorted and put 'model' last, but eh, this works just as well.
              const modelEntry = entries.find(entry => entry[0] === 'model');
              const modelId = Number(modelEntry?.[1]);
              if (modelId) {
                try {
                  const prefetchedModel = await fetchModelById(modelId);
                  dispatch(changeModel(prefetchedModel));
                } catch (e) {}
              }
            }
          }
        }
      }
    } catch (e) {}

    dispatch(finishInitializingItem());

    return {
      id,
      dehydratedState: dehydrate(queryClient),
    };
  },
);

export default ItemListFlowPage;
